/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: queueProfileApi.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    API functions for Queue Profiles.
    Centralizes all API calls related to Queue Profiles.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  ListQueueProfilesResponse,
  QueueProfileResponse,
  QueueProfile,
} from '../types';

/**
 * Payload for creating a queue profile
 */
export interface CreateQueueProfilePayload {
  name: string;
  description?: string | null;
  enabled?: boolean;
  settings?: Record<string, unknown>;
  callbackProfileId?: string | number;
}

/**
 * Payload for updating a queue profile
 */
export interface UpdateQueueProfilePayload {
  name?: string;
  description?: string | null;
  enabled?: boolean;
  settings?: Record<string, unknown>;
  callbackProfileId?: string | number;
}

/**
 * Payload for duplicating a queue profile
 */
export interface DuplicateQueueProfilePayload {
  name: string;
}

/**
 * Fetch list of queue profiles
 */
export async function fetchQueueProfiles(params: {
  page: number;
  limit: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  enabled?: boolean;
  domainId?: number;
}): Promise<ListQueueProfilesResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    ...(params.sortBy && { sortBy: params.sortBy }),
    ...(params.sortOrder && { sortOrder: params.sortOrder }),
    ...(params.search && { search: params.search }),
    ...(params.enabled != null && { enabled: params.enabled.toString() }),
    ...(params.domainId != null && { domainId: params.domainId.toString() }),
  });

  const response = await fetch(
    buildApiUrl(`/api/telephony/queue-profiles?${queryParams}`),
    {
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Failed to fetch queue profiles');
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to fetch queue profiles');
  }

  return result;
}

/**
 * Fetch all queue profiles (for quick list)
 */
export async function fetchAllQueueProfiles(): Promise<QueueProfile[]> {
  const response = await fetch(
    buildApiUrl('/api/telephony/queue-profiles?page=1&limit=1000&sortBy=name&sortOrder=asc'),
    {
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch queue profiles');
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to fetch queue profiles');
  }

  return result.data || [];
}

/**
 * Fetch single queue profile by ID
 */
export async function fetchQueueProfile(id: string | number): Promise<QueueProfileResponse> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/queue-profiles/${id}`),
    {
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

/**
 * Create queue profile
 */
export async function createQueueProfile(
  payload: CreateQueueProfilePayload
): Promise<QueueProfileResponse> {
  const response = await fetch(
    buildApiUrl('/api/telephony/queue-profiles'),
    {
      method: 'POST',
      headers: getApiHeaders(),
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

/**
 * Update queue profile
 */
export async function updateQueueProfile(
  id: string | number,
  payload: UpdateQueueProfilePayload
): Promise<QueueProfileResponse> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/queue-profiles/${id}`),
    {
      method: 'PUT',
      headers: getApiHeaders(),
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

/**
 * Delete queue profile
 */
export async function deleteQueueProfile(id: string | number): Promise<void> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/queue-profiles/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }
}

/**
 * Duplicate queue profile
 */
export async function duplicateQueueProfile(
  id: string | number,
  payload: DuplicateQueueProfilePayload
): Promise<QueueProfileResponse> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/queue-profiles/${id}/duplicate`),
    {
      method: 'POST',
      headers: getApiHeaders(),
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  return await response.json();
}


